import { motion } from "framer-motion";
import { Target, Eye, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { fadeUp, fadeRight, viewportOnce } from "@/lib/motion";
import { useSeo } from "@/hooks/useSeo";
import { VALUES, TIMELINE, WHY_CHOOSE, IMAGES, MILESTONE_ICON } from "@/data/content";

export default function About() {
  useSeo(
    "About Us | AutoDrive Tech Solutions",
    "Learn about AutoDrive Tech Solutions — a specialized automotive certification and regulatory consulting firm founded by former testing-agency professionals."
  );

  return (
    <>
      <PageHero
        overline="About Us"
        title="Engineering Trust into Every Certification"
        subtitle="A specialized regulatory consulting partner built by former agency professionals, for India's automotive manufacturers."
        image={IMAGES.corporate}
        crumb="About Us"
      />

      {/* Company Story */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading overline="Our Story" title="Built on Insider Regulatory Expertise" />
            <div className="mt-6 space-y-5 text-slate-600 leading-relaxed">
              <p>
                AutoDrive Tech Solutions was founded to solve a persistent challenge for automotive
                manufacturers in India: navigating a complex, evolving regulatory landscape without losing
                time to market. Our founders spent years inside the country's testing and certification
                agencies — and saw first-hand how the right preparation transforms outcomes.
              </p>
              <p>
                Today we bring that inside knowledge to OEMs and component suppliers, managing certification
                programs end-to-end. From applicability studies through documentation, testing coordination
                and regulatory liaison, we act as a single accountable partner focused on one goal:
                compliant products, launched faster.
              </p>
              <p>
                Our specialization spans type approval, conformity of production, homologation, and
                component & system certification across every major vehicle category — including the fast
                growing electric vehicle segment.
              </p>
            </div>
          </div>
          <Reveal variants={fadeRight} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
              <img src={IMAGES.collaborate} alt="AutoDrive engineering consulting team" className="w-full h-[520px] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6">
          {[
            { icon: Target, title: "Our Mission", text: "To simplify automotive certification for manufacturers by delivering expert, end-to-end regulatory support that reduces risk, removes uncertainty and accelerates compliant market entry across India." },
            { icon: Eye, title: "Our Vision", text: "To be India's most trusted automotive certification and regulatory consulting partner — the first name manufacturers think of when bringing new and compliant vehicles to market." },
          ].map((m, i) => (
            <Reveal key={m.title} variants={fadeUp} custom={i}>
              <div className="h-full rounded-3xl bg-white border border-slate-200 p-10">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#1E40AF] text-white">
                  <m.icon className="w-6 h-6" strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-display font-bold text-2xl text-[#0F172A]">{m.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why AutoDrive */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
          <SectionHeading overline="Why AutoDrive" title="The AutoDrive Advantage" description="Six reasons manufacturers trust us with their certification programs." className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} variants={fadeUp} custom={i % 3}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-[transform,box-shadow] duration-300">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-[#1E40AF] group-hover:bg-[#1E40AF] group-hover:text-white transition-colors">
                    <w.icon className="w-5 h-5" strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-5 font-display font-semibold text-lg text-[#0F172A]">{w.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8">
          <SectionHeading light overline="Our Values" title="Principles That Guide Our Work" className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} variants={fadeUp} custom={i}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-7 hover:bg-white/10 transition-colors">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#1E40AF] text-white">
                    <v.icon className="w-5 h-5" strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-5 font-display font-semibold text-lg text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <SectionHeading align="center" overline="Our Journey" title="How We Got Here" className="mb-16" />
          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-slate-200 md:-translate-x-1/2" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewportOnce}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`relative flex md:items-center gap-6 md:gap-0 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                >
                  <div className={`md:w-1/2 pl-16 md:pl-0 ${i % 2 === 0 ? "md:pr-14 md:text-right" : "md:pl-14"}`}>
                    <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6">
                      <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1E40AF]">{t.year}</span>
                      <h3 className="mt-2 font-display font-semibold text-lg text-[#0F172A]">{t.title}</h3>
                      <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t.text}</p>
                    </div>
                  </div>
                  <span className="absolute left-6 md:left-1/2 md:-translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-[#1E40AF] text-white ring-4 ring-white z-10">
                    <MILESTONE_ICON className="w-5 h-5" strokeWidth={1.7} />
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
